// API Configuration for React Casino Components
import axios from 'axios';

// Base API URL - use REACT_APP_API_URL env var, fallback to Render backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://test3-0ar4.onrender.com/api';

// Create axios instance with default configuration
const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // REQUIRED: send cookies/auth headers cross-origin
});

// Request interceptor to add auth token
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Adding token to request:', token.substring(0, 20) + '...');
    } else {
      console.log('No token found for request to:', config.url);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common HTTP errors
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - clear ALL session data immediately
          console.error('401 Unauthorized - clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Note: Redirection should be handled by the application logic (e.g., App.js)
          // to avoid infinite loops if public endpoints return 401 correctly.
          break;
        case 403:
          console.error('Forbidden access');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 429:
          console.error('Too many requests - rate limited');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error(`HTTP Error ${status}:`, data?.message || error.message);
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - check your connection');
    } else {
      console.error('Request error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Game API endpoints
export const gameAPI = {
  // Coinflip endpoints
  coinflip: {
    play: (data) => API.post('/game/coinflip', data),
    stats: () => API.get('/game/coinflip/stats'),
    recent: () => API.get('/game/coinflip/recent'),
  },
  
  // Upgrader endpoints
  upgrader: {
    play: (data) => API.post('/upgrader', data), // Main upgrader endpoint
    playAdvanced: (data) => API.post('/upgrader/play', data), // Alternative endpoint
    stats: () => API.get('/user/upgrader-stats'), // User upgrader stats
    recent: () => API.get('/user/recent-upgrader-games'), // Recent games
    adminStats: () => API.get('/upgrader/stats'), // Admin stats
  },
  
  // Limbo endpoints
  limbo: {
    play: (data) => API.post('/limbo/play', data),
    stats: () => API.get('/limbo/stats'),
    recent: () => API.get('/limbo/recent'),
  },
  
  // General game stats
  stats: () => API.get('/game/stats'),
  
  // online players and general stats
  onlinePlayers: () => API.get('/stats/online-players'),

  // Cases endpoints
  cases: {
    open: (data) => API.post('/cases/open', data),
  },
};

// User API endpoints
export const userAPI = {
  me: () => API.get('/auth/me'),
  profile: (data) => API.patch('/user/profile', data),
  tip: (data) => API.post('/user/tip', data),
  level: () => API.get('/user/level'),
  cases: () => API.get('/user/cases'),
  openCase: (caseType) => API.post(`/user/open-case/${caseType}`),
  caseHistory: () => API.get('/user/case-history'),
  transactions: () => API.get('/user/transactions'),
  wageringStatus: () => API.get('/user/wagering-status'),
  freeCoinsStatus: () => API.get('/user/free-coins-status'),
  claimFreeCoins: () => API.post('/user/claim-free-coins'),
  claimFreeCases: () => API.post('/user/claim-free-cases'),
  rotateSeed: () => API.post('/user/rotate-seed'),
  uploadAvatar: (formData) => API.post('/user/upload-avatar', formData),
  updateEmail: (data) => API.post('/user/update-email', data),
  claimRakeback: () => API.post('/user/claim-rakeback'),
};

// Auth API endpoints
export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  register: (data) => API.post('/auth/register', data),
  logout: () => API.post('/auth/logout'),
  me: () => API.get('/auth/me'),
  resendVerification: () => API.post('/auth/verify-email/request', {}),
  verifyEmail: (token) => API.post('/auth/verify-email', { token }),
};

// Referral API endpoints
export const referralAPI = {
  stats: () => API.get('/referral/stats'),
  claimRewards: () => API.post('/referral/claim'),
};

// Admin API endpoints (for admin users)
export const adminAPI = {
  users: (search) => API.get(`/admin/users${search ? `?search=${search}` : ''}`),
  stats: () => API.get('/admin/stats'),
  assignRole: (data) => API.post('/admin/assign-role', data),
  removeRole: (data) => API.post('/admin/remove-role', data),
  addBalance: (data) => API.post('/admin/add-balance', data),
  withdrawals: () => API.get('/admin/withdrawals'),
  approveWithdrawal: (data) => API.post('/admin/approve-withdrawal', data),
  rejectWithdrawal: (data) => API.post('/admin/reject-withdrawal', data),
};

// Promocode API endpoints
export const promocodeAPI = {
  create: (data) => API.post('/promocodes/create', data),
  toggle: (data) => API.post('/promocodes/toggle', data),
  delete: (code) => API.delete(`/promocodes/delete`, { data: { code } }),
  list: () => API.get('/promocodes'),
  redeem: (code) => API.post('/promocodes/redeem', { code }),
};

// Statistics API endpoints
export const statsAPI = {
  online: () => API.get('/stats/online-players'),
  general: () => API.get('/stats/general'),
  games: () => API.get('/stats/games'),
  leaderboard: (game) => API.get(`/stats/leaderboard${game ? `?game=${game}` : ''}`),
};

// Wallet/Transaction API endpoints
export const walletAPI = {
  deposit: (data) => API.post('/wallet/deposit', data),
  withdraw: (data) => API.post('/wallet/withdraw', data),
  transactions: () => API.get('/wallet/transactions'),
  balance: () => API.get('/wallet/balance'),
};

// Chat API endpoints
export const chatAPI = {
  messages: (limit = 50) => API.get(`/chat/messages?limit=${limit}`),
  send: (message) => API.post('/chat/send', { message }),
  mute: (userId) => API.post('/chat/mute', { userId }),
  unmute: (userId) => API.post('/chat/unmute', { userId }),
};

// Helper functions for common API patterns
export const apiHelpers = {
  // Handle API errors consistently
  handleError: (error, defaultMessage = 'An error occurred') => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  },

  // Format currency values
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  },

  // Validate bet amounts
  validateBet: (amount, balance, minBet = 0.01) => {
    const bet = parseFloat(amount);
    if (isNaN(bet) || bet <= 0) {
      return { valid: false, error: 'Please enter a valid bet amount' };
    }
    if (bet < minBet) {
      return { valid: false, error: `Minimum bet is $${minBet.toFixed(2)}` };
    }
    if (bet > balance) {
      return { valid: false, error: 'Insufficient balance' };
    }
    return { valid: true, amount: bet };
  },

  // Retry failed requests
  retryRequest: async (requestFn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  },
};

export default API;
